import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, EmptyState, LoadingState } from '../components/ui';
import { alert } from '../utils/toast';
import type { SandboxMockResponse } from '../types';
import { FlaskConical, Plus, Edit2, Trash2, Sparkles, X } from 'lucide-react';

export const SandboxMocks: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMock, setEditingMock] = useState<SandboxMockResponse | null>(null);
  const [formData, setFormData] = useState({
    route_id: '',
    http_method: 'GET' as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    status_code: 200,
    response_body: '{\n  "id": "{{uuid}}",\n  "status": "success"\n}',
    response_headers: '{}',
    delay_ms: 0,
    scenario_header_name: '',
    scenario_header_value: '',
  });

  // Fechar modal com tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: mocks, isLoading } = useQuery({
    queryKey: ['sandbox-mocks'],
    queryFn: async () => {
      const response = await api.get<SandboxMockResponse[]>('/admin/sandbox/mocks');
      return response.data || [];
    },
  });

  const { data: routes } = useQuery({
    queryKey: ['integration-routes'],
    queryFn: async () => {
      const response = await api.get<{
        routes: { id: string; endpoint_path: string; name: string }[];
      }>('/admin/integration-routes');
      return response.data.routes || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post('/admin/sandbox/mocks', {
        ...data,
        response_body: JSON.parse(data.response_body),
        response_headers: data.response_headers ? JSON.parse(data.response_headers) : undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-mocks'] });
      closeModal();
      void alert.success('Criado!', 'Resposta simulada criada com sucesso!');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string } & typeof formData) => {
      const { id, ...updateData } = data;
      const response = await api.put(`/admin/sandbox/mocks/${id}`, {
        ...updateData,
        response_body: JSON.parse(updateData.response_body),
        response_headers: updateData.response_headers
          ? JSON.parse(updateData.response_headers)
          : undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-mocks'] });
      closeModal();
      void alert.success('Atualizado!', 'Resposta simulada atualizada com sucesso!');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/sandbox/mocks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-mocks'] });
      void alert.success('Eliminado!', 'Resposta simulada eliminada com sucesso!');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      JSON.parse(formData.response_body);
      if (formData.response_headers) {
        JSON.parse(formData.response_headers);
      }
      if (editingMock) {
        updateMutation.mutate({ id: editingMock.id, ...formData });
      } else {
        createMutation.mutate(formData);
      }
    } catch {
      void alert.error('Erro', 'JSON inválido no corpo ou cabeçalhos da resposta');
    }
  };

  const handleEdit = (mock: SandboxMockResponse) => {
    setEditingMock(mock);
    setFormData({
      route_id: mock.route_id,
      http_method: mock.http_method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      status_code: mock.status_code,
      response_body: JSON.stringify(mock.response_body, null, 2),
      response_headers: mock.response_headers
        ? JSON.stringify(mock.response_headers, null, 2)
        : '{}',
      delay_ms: mock.delay_ms || 0,
      scenario_header_name: mock.scenario_header_name || '',
      scenario_header_value: mock.scenario_header_value || '',
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (mock: SandboxMockResponse) => {
    const result = await alert.confirm(
      'Eliminar Simulação?',
      `Tem a certeza de que deseja eliminar a simulação configurada para "${mock.http_method} ${mock.endpoint_path || mock.route_name}"?`
    );

    if (result.isConfirmed) {
      deleteMutation.mutate(mock.id);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingMock(null);
    setFormData({
      route_id: '',
      http_method: 'GET',
      status_code: 200,
      response_body: '{\n  "id": "{{uuid}}",\n  "status": "success"\n}',
      response_headers: '{}',
      delay_ms: 0,
      scenario_header_name: '',
      scenario_header_value: '',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Simulações Sandbox (Mocks)</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Simulações Sandbox (Mocks)</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <FlaskConical className="w-8 h-8 text-primary-500" />
            <span>Mocks de Sandbox</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Configure respostas simuladas para testes no ambiente de sandbox
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-base btn-md btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Criar Mock</span>
        </button>
      </div>

      {mocks && mocks.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {mocks.map((mock) => (
            <div
              key={mock.id}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden"
            >
              <div className="flex justify-between items-start p-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${
                      mock.http_method === 'GET'
                        ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800/50'
                        : mock.http_method === 'POST'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
                          : mock.http_method === 'PUT'
                            ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50'
                            : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50'
                    }`}
                  >
                    {mock.http_method}
                  </span>
                  <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">
                    {mock.endpoint_path || mock.route_name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleEdit(mock)}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 text-xs font-semibold"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => handleDelete(mock)}
                    className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 hover:text-rose-700 text-xs font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
              <div className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 flex flex-col space-y-2 bg-slate-50/50 dark:bg-slate-800/40">
                <div>
                  Status:{' '}
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {mock.status_code}
                  </span>{' '}
                  | Delay:{' '}
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {mock.delay_ms || 0}ms
                  </span>
                </div>
                {mock.scenario_header_name && (
                  <div>
                    <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 px-2.5 py-1 rounded-lg inline-block border border-blue-200 dark:border-blue-800/50">
                      Cenário: Ativo se o header <b>{mock.scenario_header_name}</b> ={' '}
                      <b>{mock.scenario_header_value}</b>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs text-center py-12 px-6">
          <EmptyState
            title="Nenhuma resposta de simulação configurada"
            description="Crie respostas de simulação para testar as rotas da sua aplicação no ambiente de Sandbox sem chamar os servidores externos."
          />
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-base btn-md btn-primary mt-6 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Criar a Sua Primeira Simulação</span>
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FlaskConical className="w-6 h-6 text-primary-500" />
                <span>
                  {editingMock ? 'Editar Resposta de Simulação' : 'Criar Resposta de Simulação'}
                </span>
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(createMutation.error || updateMutation.error)}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="sm:w-1/4">
                    <label htmlFor="http_method" className="label-modern">
                      Método HTTP
                    </label>
                    <select
                      id="http_method"
                      className="input-modern"
                      value={formData.http_method}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          http_method: e.target.value as typeof formData.http_method,
                        })
                      }
                      required
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                  </div>
                  <div className="sm:w-3/4">
                    <label htmlFor="route_id" className="label-modern">
                      Rota a Intercetar
                    </label>
                    <select
                      id="route_id"
                      className="input-modern font-mono"
                      value={formData.route_id}
                      onChange={(e) => setFormData({ ...formData, route_id: e.target.value })}
                      required
                    >
                      <option value="">Selecione uma rota existente...</option>
                      {routes?.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.endpoint_path} ({r.name})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">
                    Cenário de Teste (Opcional)
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Define quando esta simulação deve ser ativada baseada num Header HTTP enviado
                    pelo cliente. Se deixares em branco, será a simulação padrão.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label-modern">Nome do Header</label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="Ex: X-Mock-Scenario"
                        value={formData.scenario_header_name}
                        onChange={(e) =>
                          setFormData({ ...formData, scenario_header_name: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="label-modern">Valor do Header</label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="Ex: erro_saldo"
                        value={formData.scenario_header_value}
                        onChange={(e) =>
                          setFormData({ ...formData, scenario_header_value: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="status_code" className="label-modern">
                      Código de Estado (Status Code) *
                    </label>
                    <input
                      id="status_code"
                      type="number"
                      required
                      min="100"
                      max="599"
                      className="input-modern"
                      value={formData.status_code}
                      onChange={(e) =>
                        setFormData({ ...formData, status_code: parseInt(e.target.value) })
                      }
                    />
                  </div>

                  <div>
                    <label htmlFor="delay_ms" className="label-modern">
                      Atraso (Delay ms)
                    </label>
                    <input
                      id="delay_ms"
                      type="number"
                      min="0"
                      max="10000"
                      className="input-modern"
                      value={formData.delay_ms}
                      onChange={(e) =>
                        setFormData({ ...formData, delay_ms: parseInt(e.target.value) })
                      }
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <label
                    htmlFor="response_body"
                    className="label-modern flex justify-between items-center"
                  >
                    <span>Corpo da Resposta (JSON)</span>
                    <span
                      className="text-xs text-primary-600 dark:text-primary-400 cursor-help inline-flex items-center gap-1 normal-case"
                      title="Use {{uuid}} para gerar um ID ou {{request.body.X}} para espelhar dados do pedido."
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Suporta Variáveis Dinâmicas</span>
                    </span>
                  </label>
                  <textarea
                    id="response_body"
                    className="input-modern font-mono text-xs"
                    rows={8}
                    value={formData.response_body}
                    onChange={(e) => setFormData({ ...formData, response_body: e.target.value })}
                    required
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Exemplo: {'{"id": "{{uuid}}", "nome": "{{request.body.nome}}"}'}
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="response_headers" className="label-modern">
                  Cabeçalhos da Resposta (JSON)
                </label>
                <textarea
                  id="response_headers"
                  className="input-modern font-mono text-xs"
                  rows={4}
                  placeholder='{"Content-Type": "application/json"}'
                  value={formData.response_headers}
                  onChange={(e) => setFormData({ ...formData, response_headers: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'A guardar...'
                    : editingMock
                      ? 'Atualizar Simulação'
                      : 'Criar Simulação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
